import type { Action, GameState } from "@cloudnest/cascadia-engine";
import { applyAction, scoreGame } from "@cloudnest/cascadia-engine";
import { cascadiaEventToAction } from "./event-adapter";
import type { CascadiaReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free Cascadia replay simulation. Mirrors the live store's
 * `replayEventList` (`init` seeds canonical state; every other event is
 * reconstructed via the event adapter and folded through the engine reducer
 * `applyAction`) but fails loudly when a fixture event is unsupported or
 * rejected by the engine, so a malformed fixture cannot silently drop steps.
 *
 * Imports only the engine — no React, stores, auth, router, or WebSocket.
 */
export interface CascadiaReplaySnapshot {
  readonly index: number;
  readonly event: CascadiaReplayFixtureEvent;
  /** The reconstructed engine action, or null for the `init` snapshot. */
  readonly action: Action | null;
  readonly state: GameState;
}

function requireState(state: GameState | null, eventType: string): GameState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

export function buildCascadiaReplaySnapshots(
  events: readonly CascadiaReplayFixtureEvent[],
): readonly CascadiaReplaySnapshot[] {
  const snapshots: CascadiaReplaySnapshot[] = [];
  let state: GameState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = (event.payload as { state: GameState }).state;
      snapshots.push({
        index: snapshots.length,
        event,
        action: null,
        state,
      });
      continue;
    }

    const current = requireState(state, event.eventType);
    if (event.eventType === "ScoreGame") {
      const result = scoreGame(current);
      if (!result.ok) {
        throw new Error(
          `${event.eventType} (#${event.sequenceNumber}) rejected: ${result.error}`,
        );
      }
      state = {
        ...result.value,
        version: current.version + 1,
      };
      snapshots.push({
        index: snapshots.length,
        event,
        action: null,
        state,
      });
      continue;
    }

    const action = cascadiaEventToAction(event.eventType, event.payload);
    const result = applyAction(current, action);
    if (!result.ok) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.validation.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push({
      index: snapshots.length,
      event,
      action,
      state,
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Cascadia event log");
  }

  return snapshots;
}
